/**
 * Dupe Finder — <pb-comparator>
 *
 * Floating launcher + modal that lets a customer search a designer fragrance and see
 * Perfume Bay's interpretation of it priced against the original's RRP.
 *
 * Everything the customer can buy is a real Shopify variant, added through the theme's own
 * cart plumbing. There are no third-party products and no outbound links.
 *
 * All data-derived text is written with textContent. Nothing from the dataset reaches innerHTML.
 */

import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent } from '@theme/events';
import { buildIndex, search, searchScored, parseOriginal, parseCode, normalise } from '@theme/pb-comparator-search';
import { resolveBrand, parseBrandOverrides, expandAliases } from '@theme/pb-comparator-brands';
import { CATALOGUE, CATALOGUE_BRANDS, CATALOGUE_GENDERS } from '@theme/pb-comparator-catalogue';

const ADDED_FEEDBACK_MS = 2500;
const AUTOCOMPLETE_MIN_CHARS = 2;

const CACHE_KEY = 'pb-comparator-data';

// Storefront parameters that pin a request to a specific (usually unpublished) theme. A bare
// path is served by the PUBLISHED theme, which during development does not have the JSON
// template and so returns a themed HTML page instead.
const PREVIEW_PARAMS = ['preview_theme_id', '_ab', '_fd', '_sc'];

// Suffix of templates/page.pb-comparator-feed.liquid. Requesting the page with ?view= forces that
// template regardless of the Theme template dropdown on the page in admin, which is a shop-level
// setting no one can see is load-bearing. It has silently reset to "Default page" before, and the
// symptom is a themed HTML page arriving where JSON was expected. Pinning it here means the
// endpoint depends only on files in this repo.
//
// The suffix deliberately does not match the page handle. Opening a page in the theme editor makes
// Shopify generate page.<suffix>.json for that page, and a theme cannot hold both a .json and a
// .liquid for one suffix: the pair resolves inconsistently across Shopify's app servers, so the
// endpoint returns JSON on some requests and a themed HTML page on others. Naming the template
// after something the editor will never generate removes that whole class of failure.
const DATA_TEMPLATE_VIEW = 'pb-comparator-feed';

// Short enough that a merchant adding a fragrance sees it without clearing anything, long enough
// that a browsing session still fetches the dataset roughly once. An indefinite session cache
// made edits look like they had silently failed.
const CACHE_TTL_MS = 15 * 60 * 1000;

// A brand search ("dior") can match many originals. Cap the groups so the modal stays scannable.
const MAX_GROUPS = 12;

// A full pyramid across several groups runs to dozens of notes; this many stays browsable.
const MAX_NOTE_CHIPS = 12;

// Below three shared notes the overlap is commodity material — bergamot, musk, vanilla — which
// most of the catalogue carries. Two scents sharing only those are not alike in any way a customer
// would recognise, and presenting them as similar reads as a random list.
const MIN_SHARED_NOTES = 3;

/**
 * @typedef {Object} Fragrance
 * @property {string} id
 * @property {string} brand
 * @property {string} name
 * @property {string} gender
 * @property {string} family
 * @property {string[]} aliases
 * @property {{ top: string[], heart: string[], base: string[] }} notes
 * @property {number} rrp - minor units
 * @property {number} rrp_size - ml
 * @property {string} rrp_note
 * @property {string} image
 */

/**
 * @typedef {Object} Offer
 * @property {string} fragrance - fragrance id
 * @property {number} product_id
 * @property {number} variant_id
 * @property {string} title
 * @property {string} variant_title
 * @property {string} url
 * @property {string} image
 * @property {number} price - minor units
 * @property {number} size_ml
 * @property {boolean} available
 */

/**
 * @typedef {Object} Group
 * @property {Fragrance} fragrance
 * @property {Offer[]} offers
 * @property {number} score
 * @property {number} bestSaving
 */

/**
 * Setup mistakes (missing page, unassigned template) are indistinguishable from real outages to
 * the shopper, who sees the same friendly message either way. The developer needs the specifics,
 * so they go to the console rather than the screen.
 *
 * @param {unknown} error
 */
function report(error) {
  const detail = error instanceof Error ? error.message : String(error);
  console.error(`[Dupe Finder] ${detail}`);
}

/**
 * @param {string} tag
 * @param {string} [className]
 * @param {string} [text]
 * @returns {HTMLElement}
 */
function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/**
 * Only allow image URLs we could plausibly have produced (Shopify CDN, same origin).
 * Guards against a malformed metaobject value turning into a javascript: or data: URL.
 *
 * @param {unknown} url
 * @returns {string}
 */
function safeImageUrl(url) {
  if (typeof url !== 'string' || !url) return '';
  if (url.startsWith('//') || url.startsWith('/')) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === 'https:' ? parsed.href : '';
  } catch {
    return '';
  }
}

/**
 * The curated row for a product code, following the CAR range's mirror of the BEY one.
 *
 * Twenty-three products are coded CAR### and are absent from the sheet, but their numbers mirror
 * the BEY range exactly: of the 23, eighteen carry a scent name identical to their BEY twin and
 * two are deliberate variants of it ("Homme Intense 2011" against "Homme Intense"). That is strong
 * enough to inherit brand and gender across.
 *
 * The name is NOT inherited by callers that already have one — CAR130 and CAR131 name a different
 * release from their twin, and collapsing them into it would merge two products the merchant
 * deliberately keeps apart. Only a product with no parsable name at all falls back to it.
 *
 * @param {string} code
 * @returns {{ brand: string, name: string, gender: string }|undefined}
 */
function curatedEntry(code) {
  if (!code) return undefined;
  const own = CATALOGUE[code];
  if (own) return own;
  return code.startsWith('CAR') ? CATALOGUE[`BEY${code.slice(3)}`] : undefined;
}

class PbComparator extends HTMLElement {
  /** @type {Fragrance[]|null} */
  #index = null;
  /** @type {Offer[]} */
  #offers = [];
  /** @type {Object|null} */
  #dataset = null;
  /** @type {Promise<void>|null} */
  #loadPromise = null;
  /** @type {Group[]} */
  #groups = [];
  /** @type {Map<string, string>} */
  #brandOverrides = new Map();
  /** Fallback when a product gives an original price but no size for it. */
  #defaultOriginalSize = 100;
  /** Fallback bottle size for our own products, none of which record one. */
  #defaultProductSize = 0;
  /** @type {'cheapest'|'priciest'} */
  #sort = 'cheapest';
  /** @type {'grid'|'list'} */
  #layout = 'grid';
  #activeSuggestion = -1;
  /** @type {Array<{fragrance: Fragrance, reason: string, detail: string}>} */
  #suggestions = [];
  /**
   * Set when the results on screen are stand-ins rather than the answer to the search.
   * @type {{ reason: 'no_result'|'no_offers', query: string }|null}
   */
  #fallback = null;
  /** @type {Record<string, string>} */
  #strings = {};
  /** @type {string} */
  endpoint = '';

  connectedCallback() {
    this.endpoint = this.dataset.endpoint || '';
    this.#strings = this.#readStrings();
    this.#brandOverrides = parseBrandOverrides(this.#readJson('[data-pb-brand-overrides]'), normalise);

    const configuredSize = Number(this.dataset.defaultOriginalSize);
    if (configuredSize > 0) this.#defaultOriginalSize = configuredSize;

    const configuredProductSize = Number(this.dataset.defaultProductSize);
    if (configuredProductSize > 0) this.#defaultProductSize = configuredProductSize;

    this.dialog = this.querySelector('dialog');
    this.launcher = this.querySelector('[data-pb-launcher]');
    this.searchInput = this.querySelector('[data-pb-search-input]');
    this.suggestionList = this.querySelector('[data-pb-suggestions]');
    this.searchScreen = this.querySelector('[data-pb-screen="search"]');
    this.resultsScreen = this.querySelector('[data-pb-screen="results"]');
    this.resultsBody = this.querySelector('[data-pb-results-body]');
    this.statusRegion = this.querySelector('[data-pb-status]');

    if (!this.dialog || !this.launcher || !this.searchInput) return;

    this.launcher.addEventListener('click', this.#open);
    // Warm the dataset on intent, so the first open has nothing to wait for.
    this.launcher.addEventListener('pointerenter', this.#warm, { once: true });
    this.launcher.addEventListener('focus', this.#warm, { once: true });

    this.querySelectorAll('[data-pb-close]').forEach((node) => node.addEventListener('click', this.#close));
    this.clearButton = this.querySelector('[data-pb-clear]');
    this.clearButton?.addEventListener('click', this.#clearSearch);
    this.querySelector('[data-pb-search-again]')?.addEventListener('click', this.#showSearchScreen);

    this.searchInput.addEventListener('input', this.#handleInput);
    this.searchInput.addEventListener('keydown', this.#handleSearchKeydown);
    this.querySelector('[data-pb-search-form]')?.addEventListener('submit', this.#handleSubmit);

    this.dialog.addEventListener('close', this.#handleDialogClose);
    this.dialog.addEventListener('click', this.#handleBackdropClick);

    this.addEventListener('click', this.#handleDelegatedClick);

    // Anything on the page may open the finder by carrying [data-pb-open-comparator] — the search
    // page's empty state does, because a search that found nothing on a dupe store usually means
    // the shopper named an original we stock under a different name, which is exactly this.
    //
    // Delegated from the document rather than bound to the elements: those triggers live inside
    // sections that re-render through `morph`, which replaces the nodes and would silently drop a
    // direct listener.
    document.addEventListener('click', this.#handleExternalTrigger);

    // Prefetch once the page is otherwise idle. Costs nothing on the critical path.
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => this.#warm(), { timeout: 4000 });
    }
  }

  disconnectedCallback() {
    document.body.style.removeProperty('overflow');
    document.removeEventListener('click', this.#handleExternalTrigger);
  }

  /**
   * Open the finder from a trigger outside this element.
   *
   * @param {MouseEvent} event
   */
  #handleExternalTrigger = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-pb-open-comparator]') : null;
    if (!target) return;

    event.preventDefault();
    this.#open();
  };

  #readStrings() {
    const node = this.querySelector('[data-pb-strings]');
    if (!node?.textContent) return {};
    try {
      return JSON.parse(node.textContent);
    } catch {
      return {};
    }
  }

  /**
   * @param {string} selector
   * @returns {string}
   */
  #readJson(selector) {
    const node = this.querySelector(selector);
    if (!node?.textContent) return '';
    try {
      return JSON.parse(node.textContent);
    } catch {
      return '';
    }
  }

  /**
   * Placeholders are written as [name] rather than {{ name }} so that Liquid's `t` filter
   * never tries to interpolate them on the way through the section.
   *
   * @param {string} key
   * @param {Record<string, string|number>} [vars]
   * @returns {string}
   */
  #t(key, vars) {
    let value = this.#strings[key] ?? key;
    if (vars) {
      for (const [name, replacement] of Object.entries(vars)) {
        value = value.split(`[${name}]`).join(String(replacement));
      }
    }
    return value;
  }

  /**
   * @param {number} minorUnits
   * @returns {string}
   */
  #money(minorUnits) {
    const format = this.#dataset?.money_format || '{{amount}}';
    const currency = this.#dataset?.currency || 'EUR';
    return formatMoney(Math.round(minorUnits), format, currency);
  }

  /**
   * Turn the flat product list into the originals the comparator actually shows.
   *
   * Products name their original inside the note-group entry title, so several products can
   * resolve to the same original and are collapsed into one group. Anything whose title does not
   * follow the "Inspired By" convention is skipped and counted, so a mis-titled product shows up
   * as a number in the console rather than as a search result nobody notices is missing.
   *
   * @param {Array<Object>} products
   * @returns {number} how many originals were built
   */
  #buildCatalogue(products) {
    /** @type {Map<string, Fragrance & { offers: Offer[] }>} */
    const originals = new Map();
    const skipped = [];

    for (const product of products) {
      // Entry title first — it is the field the merchant curates. Handle is a lossy fallback
      // (spaces became dashes), product title the last resort.
      const parsed =
        parseOriginal(product.entry_title) ||
        parseOriginal(String(product.entry_handle || '').replace(/-/g, ' ')) ||
        parseOriginal(product.title);

      // The code survives titles the name parser rejects, and the curated sheet can supply the
      // rest from it. Without this a single missing "By" drops a product out of search entirely.
      const code = parsed?.code || parseCode(product.entry_title) || parseCode(product.title);
      const curated = curatedEntry(code);
      const name = parsed?.name || curated?.name || '';

      if (!name) {
        skipped.push(product.title);
        continue;
      }

      const key = normalise(name);
      let original = originals.get(key);

      if (!original) {
        original = {
          id: key,
          name,
          brand: this.#brandFor(name, code, product.brand),
          gender: '',
          family: '',
          aliases: [],
          notes: { top: [], heart: [], base: [] },
          rrp: 0,
          rrp_size: 0,
          rrp_note: '',
          image: '',
          offers: [],
        };
        originals.set(key, original);
      } else if (!original.brand && product.brand) {
        // A scent groups several products — a 50ml, a car diffuser — and only the first to arrive
        // creates the group. If that one had no metafield and a sibling does, take it, rather than
        // leaving the whole group unbranded over the order the feed happened to be in.
        original.brand = String(product.brand).trim();
      }

      if (code && !original.aliases.includes(code)) original.aliases.push(code);

      // First product carrying notes wins. Products interpreting the same original have
      // near-identical note lists, so merging them would only produce duplicates.
      const notes = product.notes || {};
      const hasNotes = notes.top?.length || notes.heart?.length || notes.base?.length;
      const hasOwnNotes =
        original.notes.top.length || original.notes.heart.length || original.notes.base.length;
      if (hasNotes && !hasOwnNotes) {
        original.notes = { top: notes.top || [], heart: notes.heart || [], base: notes.base || [] };
      }

      // The curated sheet outranks the product tags, which disagree with it on 22 products and are
      // demonstrably the wrong one of the two — Miss Dior is tagged as menswear.
      const curatedGender = curated?.gender || CATALOGUE_GENDERS[key] || '';
      if (curatedGender) original.gender = curatedGender;
      else if (!original.gender && product.gender) original.gender = product.gender;

      if (!original.rrp && product.original_price > 0) {
        original.rrp = product.original_price;
        original.rrp_size = product.original_size_ml > 0 ? product.original_size_ml : this.#defaultOriginalSize;
      }

      for (const variant of product.variants || []) {
        original.offers.push({
          fragrance: key,
          code,
          product_id: product.product_id,
          variant_id: variant.variant_id,
          title: product.title,
          variant_title: variant.variant_title,
          url: product.url,
          image: product.image,
          price: variant.price,
          size_ml: variant.size_ml,
          available: variant.available,
        });
      }
    }

    const catalogue = [...originals.values()];

    this.#index = buildIndex(catalogue);
    this.#offers = catalogue.flatMap((original) => original.offers);

    if (skipped.length) {
      report(
        `${skipped.length} product(s) skipped — no "Inspired By" pattern in their note-group ` +
          `entry or product title. First few: ${skipped.slice(0, 5).join(' | ')}`
      );
    }

    return catalogue.length;
  }

  /**
   * Resolve the data URL against the theme the shopper is actually looking at, pinning the
   * template with ?view= so the page's Theme template setting cannot break the endpoint.
   *
   * While a theme is unpublished, storefront URLs carry preview parameters. Fetching a bare
   * path drops them, so the request is served by the published theme — which during development
   * has no `pb-comparator-feed` template and returns a themed HTML page. The result is a comparator
   * that errors on a preview theme while the endpoint looks perfectly healthy in the address bar,
   * because the address bar has the parameters and the fetch did not.
   *
   * @returns {{ url: string, themeKey: string }}
   */
  #resolveEndpoint() {
    const url = new URL(this.endpoint, window.location.origin);
    const current = new URLSearchParams(window.location.search);
    let themeKey = 'live';

    // Left alone if the configured handle already pins a template, so a merchant pointing this at
    // a differently named page is not overridden.
    if (!url.searchParams.has('view')) url.searchParams.set('view', DATA_TEMPLATE_VIEW);

    for (const key of PREVIEW_PARAMS) {
      const value = current.get(key);
      if (!value) continue;

      url.searchParams.set(key, value);
      if (key === 'preview_theme_id') themeKey = value;
    }

    return { url: url.toString(), themeKey };
  }

  #warm = () => {
    this.#load().catch(report);
  };

  /**
   * Fetch the dataset once. sessionStorage survives navigation within the visit, which is the
   * common case — a customer opens the comparator on several product pages in one session.
   */
  #load() {
    if (this.#loadPromise) return this.#loadPromise;

    this.#loadPromise = (async () => {
      let payload = null;

      let fromNetwork = false;
      const { url: endpointUrl, themeKey } = this.#resolveEndpoint();
      // Keyed by theme so switching between a preview and the live theme cannot serve the
      // other one's dataset from cache.
      const cacheKey = `${CACHE_KEY}:${themeKey}`;

      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const envelope = JSON.parse(cached);
          const fresh = typeof envelope?.at === 'number' && Date.now() - envelope.at < CACHE_TTL_MS;
          if (fresh) payload = envelope.data;
        }
      } catch {
        payload = null;
      }

      if (!payload) {
        if (!this.endpoint) {
          throw new Error('Data page handle is not set in the Dupe Finder section settings.');
        }

        fromNetwork = true;

        // Deliberately no Accept header. Sending `Accept: application/json` makes Shopify serve
        // its own page-resource JSON ({"page":{"id":…,"body_html":…}}) instead of rendering this
        // page's template, so the response parses fine and contains none of our data. Requesting
        // it as an ordinary page document is what returns the template output.
        const response = await fetch(endpointUrl);

        if (response.status === 404) {
          throw new Error(
            `No page found at ${endpointUrl}. Create a page with that handle — its Theme template ` +
              'setting does not matter, the URL pins the template itself.'
          );
        }

        if (!response.ok) {
          throw new Error(`Request to ${endpointUrl} failed with status ${response.status}.`);
        }

        const body = await response.text();

        try {
          payload = JSON.parse(body);
        } catch {
          // A themed HTML page came back instead of JSON. The URL pins the template, so the page's
          // own template assignment is not the cause — the theme serving this request does not
          // contain templates/page.pb-comparator-feed.liquid and Shopify fell back to the default
          // page template. Deploy the theme; if it persists, the file failed to sync.
          throw new Error(
            `${endpointUrl} returned HTML instead of JSON. The theme serving this request has no ` +
              '"page.pb-comparator-feed.liquid" template — deploy the theme, or check that the ' +
              'file reached it.'
          );
        }

      }

      // A payload without a products array means the endpoint and this script are different
      // versions — usually mid-deploy. Say so rather than quietly building an empty catalogue,
      // which looks identical to "we stock nothing you searched for".
      if (!Array.isArray(payload.products)) {
        throw new Error(
          'Data endpoint returned no "products" array. The page template and this script are ' +
            'probably different versions — redeploy the theme.'
        );
      }

      this.#dataset = payload;
      const built = this.#buildCatalogue(payload.products);

      // Never cache an empty catalogue. Doing so turns a momentary deploy mismatch into a
      // 15-minute outage that survives reloads, with nothing on screen to explain it.
      if (fromNetwork && built > 0) {
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), data: payload }));
        } catch {
          // Quota or private mode. Not fatal — we just refetch next navigation.
        }
      }

      if (built === 0) {
        report(
          `Loaded ${payload.products.length} product(s) but built 0 searchable fragrances. ` +
            'Check that product titles or note-group entry titles contain "Inspired By".'
        );
      }
    })();

    this.#loadPromise.catch(() => {
      // Allow a later attempt to retry rather than caching the rejection forever.
      this.#loadPromise = null;
    });

    return this.#loadPromise;
  }

  #open = async () => {
    this.dialog.showModal();
    document.body.style.overflow = 'hidden';
    this.#track('comparator_opened');

    try {
      await this.#load();
      window.requestAnimationFrame(() => this.searchInput.focus());
    } catch (error) {
      report(error);
      this.#renderError();
    }
  };

  #close = () => {
    if (this.dialog.open) this.dialog.close();
  };

  #handleDialogClose = () => {
    document.body.style.removeProperty('overflow');
    this.#track('comparator_closed');
  };

  #handleBackdropClick = (event) => {
    // A click landing on the dialog element itself (not the panel inside) is a backdrop click.
    if (event.target === this.dialog) this.#close();
  };

  #handleInput = async () => {
    const query = this.searchInput.value.trim();
    this.#toggleClearButton();

    if (query.length < AUTOCOMPLETE_MIN_CHARS) {
      this.#clearSuggestions();
      return;
    }

    if (!this.#index) {
      try {
        await this.#load();
      } catch (error) {
        report(error);
        return;
      }
      // The customer may have kept typing or cleared the field while we waited.
      if (this.searchInput.value.trim() !== query) return;
    }

    this.#suggestions = searchScored(this.#index, expandAliases(query, normalise), 8);
    this.#activeSuggestion = -1;
    this.#renderSuggestions();
  };

  #handleSearchKeydown = (event) => {
    if (!this.#suggestions.length) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const count = this.#suggestions.length;
      this.#activeSuggestion = (this.#activeSuggestion + direction + count) % count;
      this.#renderSuggestions();
      return;
    }

    if (event.key === 'Enter' && this.#activeSuggestion >= 0) {
      event.preventDefault();
      this.#showFragrance(this.#suggestions[this.#activeSuggestion].fragrance);
      return;
    }

    if (event.key === 'Escape' && this.#suggestions.length) {
      event.stopPropagation();
      this.#clearSuggestions();
    }
  };

  #handleSubmit = async (event) => {
    event.preventDefault();
    await this.#runSearch(this.searchInput.value.trim());
  };

  /**
   * Run a search and render it. Shared by the search form and the note chips, so a chip behaves
   * exactly like typing that note — including landing in the input, so it is obvious what was
   * searched and easy to edit.
   *
   * @param {string} query
   */
  async #runSearch(query) {
    if (!query) return;

    if (!this.#index) {
      try {
        await this.#load();
      } catch (error) {
        report(error);
        this.#renderError();
        return;
      }
    }

    this.#track('search_started', { query });
    this.#fallback = null;

    // Every original the query matches, not just the best one — a brand search like "dior"
    // should list the whole brand, each with our interpretations beneath it.
    //
    // Abbreviations are expanded here rather than inside the engine: the engine stays a pure
    // string matcher that knows nothing about perfume houses, and only one string is rewritten.
    // `query` itself is untouched so "Nothing found for LV" quotes what was actually typed.
    const matches = searchScored(this.#index, expandAliases(query, normalise), MAX_GROUPS * 2);

    if (!matches.length) {
      this.#track('search_no_result', { query });

      // Nothing recognised. There is no anchor to measure similarity against, so rather than
      // guess a relationship that does not exist, offer the best-covered scents and say plainly
      // that these are suggestions, not a match.
      const suggestions = this.#mostCovered();
      if (suggestions.length) {
        this.#fallback = { reason: 'no_result', query };
        this.#showGroups(suggestions);
        return;
      }

      this.#renderNoResults(query);
      this.#showResultsScreen();
      return;
    }

    this.#showGroups(matches);

    if (!this.#groups.length) {
      // Recognised the original but carry no interpretation of it. Here we do have an anchor —
      // the matched fragrance's own pyramid — so the alternatives can be genuinely related.
      this.#track('search_no_result', { query, reason: 'no_offers' });

      const similar = this.#similarByNotes(matches.map((match) => match.fragrance));
      if (similar.length) {
        this.#fallback = { reason: 'no_offers', query };
        this.#showGroups(similar);
        return;
      }

      this.#renderNoMatch();
      return;
    }

    this.#track('search_completed', {
      query,
      originals: this.#groups.length,
      results: this.#groups.reduce((total, group) => total + group.offers.length, 0),
    });
  }

  #handleDelegatedClick = (event) => {
    const suggestion = event.target.closest('[data-pb-suggestion-index]');
    if (suggestion) {
      const idx = Number(suggestion.dataset.pbSuggestionIndex);
      if (this.#suggestions[idx]) this.#showFragrance(this.#suggestions[idx].fragrance);
      return;
    }

    const noteChip = event.target.closest('[data-pb-note]');
    if (noteChip) {
      const note = noteChip.dataset.pbNote;
      this.searchInput.value = note;
      this.#track('note_explored', { note });
      this.#runSearch(note).catch(report);
      this.resultsBody?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      return;
    }

    const sortButton = event.target.closest('[data-pb-sort]');
    if (sortButton) {
      this.#sort = sortButton.dataset.pbSort === 'priciest' ? 'priciest' : 'cheapest';
      this.#track('sort_changed', { sort: this.#sort });
      this.#renderResults();
      return;
    }

    const layoutButton = event.target.closest('[data-pb-layout]');
    if (layoutButton) {
      this.#layout = layoutButton.dataset.pbLayout === 'list' ? 'list' : 'grid';
      this.#renderResults();
      return;
    }

    const addButton = event.target.closest('[data-pb-add]');
    if (addButton) {
      this.#addToCart(addButton);
      return;
    }

    const productLink = event.target.closest('[data-pb-product-link]');
    if (productLink) {
      this.#track('own_product_viewed', { url: productLink.getAttribute('href') });
    }
  };

  #clearSuggestions() {
    this.#suggestions = [];
    this.#activeSuggestion = -1;
    if (this.suggestionList) {
      this.suggestionList.replaceChildren();
      this.suggestionList.hidden = true;
    }
    this.searchInput?.setAttribute('aria-expanded', 'false');
    this.searchInput?.removeAttribute('aria-activedescendant');
    this.classList.remove('pb-comparator--suggesting');
  }

  #renderSuggestions() {
    if (!this.suggestionList) return;

    if (!this.#suggestions.length) {
      this.#clearSuggestions();
      return;
    }

    const items = this.#suggestions.map(({ fragrance, reason, detail }, i) => {
      const item = el('li', 'pb-comparator__suggestion');
      item.setAttribute('role', 'presentation');

      const button = el('button', 'pb-comparator__suggestion-button');
      button.type = 'button';
      button.id = `pb-comparator-suggestion-${i}`;
      button.dataset.pbSuggestionIndex = String(i);
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', String(i === this.#activeSuggestion));
      if (i === this.#activeSuggestion) button.classList.add('is-active');

      const text = el('span', 'pb-comparator__suggestion-text');
      text.append(el('span', 'pb-comparator__suggestion-name', fragrance.name));

      // One secondary line, and the brand earns it over anything else. A note match takes the
      // slot only when there is no brand, since it is the only thing explaining that result.
      let sub = fragrance.brand;
      if (!sub && reason === 'note' && detail) sub = this.#t('matched_note', { note: detail });
      if (sub) text.append(el('span', 'pb-comparator__suggestion-sub', sub));
      button.append(text);

      const gender = (fragrance.gender || '').toLowerCase();
      const genderLabel = gender ? this.#t(`gender_${gender}`) : '';
      if (genderLabel && genderLabel !== `gender_${gender}`) {
        button.append(el('span', `pb-comparator__gender-pill pb-comparator__gender-pill--${gender}`, genderLabel));
      }

      item.append(button);
      return item;
    });

    this.suggestionList.replaceChildren(...items);
    this.suggestionList.hidden = false;
    this.searchInput.setAttribute('aria-expanded', 'true');
    this.classList.add('pb-comparator--suggesting');

    if (this.#activeSuggestion >= 0) {
      this.searchInput.setAttribute('aria-activedescendant', `pb-comparator-suggestion-${this.#activeSuggestion}`);
    } else {
      this.searchInput.removeAttribute('aria-activedescendant');
    }
  }

  #clearSearch = () => {
    this.searchInput.value = '';
    this.#clearSuggestions();
    this.#toggleClearButton();
    this.searchInput.focus();
  };

  /** The clear affordance only makes sense once there is something to clear. */
  #toggleClearButton() {
    if (this.clearButton) this.clearButton.hidden = !this.searchInput.value;
  }

  #showSearchScreen = () => {
    this.searchScreen.hidden = false;
    this.resultsScreen.hidden = true;
    this.#groups = [];
    this.searchInput.value = '';
    this.#clearSuggestions();
    this.searchInput.focus();
    this.#track('search_again_clicked');
  };

  #showResultsScreen() {
    this.searchScreen.hidden = true;
    this.resultsScreen.hidden = false;
    this.#clearSuggestions();
  }

  /**
   * Build the result groups for a set of matched originals.
   *
   * Originals we carry no interpretation of are dropped: the list exists to sell our products,
   * and a heading with nothing under it is just a dead end for the customer.
   *
   * Ordering is by match score first, then by the best saving we can offer. That keeps an exact
   * search ("dior sauvage") pinned to the right fragrance, while a brand search ("dior") — where
   * everything ties on score — leads with the most compelling deal rather than alphabetically.
   *
   * @param {Array<{ fragrance: Fragrance, score: number }>} scored
   */
  #showGroups(scored) {
    const groups = [];

    for (const { fragrance, score } of scored) {
      const offers = this.#offersFor(fragrance.id);
      if (!offers.length) continue;

      const bestSaving = offers.reduce((best, offer) => {
        const savings = this.#savings(fragrance, offer);
        return savings && savings.percent > best ? savings.percent : best;
      }, 0);

      groups.push({ fragrance, offers, score, bestSaving });
    }

    groups.sort((a, b) => (b.score !== a.score ? b.score - a.score : b.bestSaving - a.bestSaving));

    this.#groups = groups.slice(0, MAX_GROUPS);
    this.#sort = 'cheapest';
    this.#clearSuggestions();
    this.#showResultsScreen();
    this.#renderResults();
  }

  /**
   * @param {Fragrance} fragrance
   */
  #showFragrance(fragrance) {
    this.#fallback = null;
    this.#showGroups([{ fragrance, score: 100 }]);

    if (!this.#groups.length) {
      // Picked from the suggestion list, so the fragrance is certain — only our stock is missing.
      const similar = this.#similarByNotes([fragrance]);
      if (similar.length) {
        this.#fallback = { reason: 'no_offers', query: fragrance.name };
        this.#showGroups(similar);
        return;
      }

      this.#renderNoMatch();
      return;
    }

    this.#track('search_completed', {
      fragrance: fragrance.id,
      results: this.#groups[0].offers.length,
    });
  }

  /**
   * Resolve a scent to its house.
   *
   * Five sources, most authoritative first. The merchant's override setting stays on top so a wrong
   * entry can be corrected from the theme editor without waiting for a deploy. The product's own
   * `custom.merk` comes next: it is the only source that is current by construction, since it is
   * typed on the product itself and needs neither a re-export nor a deploy — which is what lets a
   * newly added fragrance be found by its house the moment it is published. Then the curated sheet,
   * compiled by hand for exactly this; then the sheet's name index, which catches products whose
   * code it does not list (the CAR range mirrors the BEY one, so those resolve by scent name); and
   * the built-in guess table last.
   *
   * @param {string} name
   * @param {string} code
   * @param {string} [storeBrand] - `custom.merk` as published in the feed
   * @returns {string} brand, or '' when nothing knows it
   */
  #brandFor(name, code, storeBrand) {
    const override = this.#brandOverrides.get(normalise(name));
    if (override) return override;

    const fromStore = String(storeBrand || '').trim();
    if (fromStore) return fromStore;

    const curated = curatedEntry(code);
    if (curated?.brand) return curated.brand;

    const byName = CATALOGUE_BRANDS[normalise(name)];
    if (byName) return byName;

    return resolveBrand(name, this.#brandOverrides, normalise);
  }

  /**
   * @param {string} fragranceId
   * @returns {Offer[]}
   */
  #offersFor(fragranceId) {
    return this.#offers.filter((offer) => offer.fragrance === fragranceId);
  }

  /**
   * Scents sharing the most notes with the ones searched for.
   *
   * Used when we recognise the fragrance but stock no interpretation of it. Shared notes are the
   * only similarity signal in the data worth trusting — brand tells you nothing about how
   * something smells, and two scents from one house are often nothing alike.
   *
   * The floor of MIN_SHARED_NOTES matters more than the ranking: below it the overlap is usually
   * one commodity note like bergamot, which every third fragrance carries. Suggesting those as
   * "similar" reads as a random list and costs more trust than showing nothing.
   *
   * @param {Fragrance[]} seeds - the fragrances the query matched
   * @returns {Array<{ fragrance: Fragrance, score: number }>} best overlap first
   */
  #similarByNotes(seeds) {
    /** @type {Set<string>} */
    const wanted = new Set();
    for (const seed of seeds) {
      for (const note of this.#notesFor(seed)) {
        const key = normalise(note);
        if (key) wanted.add(key);
      }
    }

    if (!wanted.size) return [];

    const seedIds = new Set(seeds.map((seed) => seed.id));
    const scored = [];

    for (const fragrance of this.#index || []) {
      if (seedIds.has(fragrance.id)) continue;
      if (!this.#offersFor(fragrance.id).length) continue;

      let shared = 0;
      for (const note of this.#notesFor(fragrance)) {
        if (wanted.has(normalise(note))) shared++;
      }

      if (shared >= MIN_SHARED_NOTES) scored.push({ fragrance, score: shared });
    }

    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, MAX_GROUPS);
  }

  /**
   * The scents we carry the most interpretations of.
   *
   * The last resort, when the query matched nothing at all. Offer count is a deliberately honest
   * proxy: it is what we can actually show, not a popularity claim the data cannot support.
   *
   * @returns {Array<{ fragrance: Fragrance, score: number }>}
   */
  #mostCovered() {
    const scored = [];

    for (const fragrance of this.#index || []) {
      const offers = this.#offersFor(fragrance.id);
      if (offers.length) scored.push({ fragrance, score: offers.length });
    }

    scored.sort((a, b) => b.score - a.score || a.fragrance.name.localeCompare(b.fragrance.name));
    return scored.slice(0, MAX_GROUPS);
  }

  /**
   * Savings are computed at equal volume: a 50 ml interpretation is compared against 50 ml of
   * the original, never against the original's 100 ml bottle price.
   *
   * @param {Fragrance} fragrance
   * @param {Offer} offer
   * @returns {{ equivalent: number, saving: number, percent: number }|null}
   */
  #savings(fragrance, offer) {
    const rrp = Number(fragrance.rrp) || 0;
    const rrpSize = Number(fragrance.rrp_size) || 0;
    const size = Number(offer.size_ml) || this.#defaultProductSize;

    if (rrp <= 0 || rrpSize <= 0 || size <= 0) return null;

    const equivalent = (rrp / rrpSize) * size;
    const saving = equivalent - offer.price;
    if (saving <= 0) return null;

    return { equivalent, saving, percent: Math.round((saving / equivalent) * 100) };
  }

  #renderResults() {
    if (!this.resultsBody) return;

    if (!this.#groups.length) {
      this.#renderNoMatch();
      return;
    }

    const fragments = document.createDocumentFragment();
    const single = this.#groups.length === 1;

    // Says up front that these are not what was asked for. Placed above the results rather than
    // below, because a shopper who scrolled straight into a list would otherwise read it as the
    // answer to their search.
    const notice = this.#buildFallbackNotice();
    if (notice) fragments.append(notice);

    // The olfactive box describes one fragrance, so it only makes sense on a single-result view.
    // On a brand search each group carries its own notes line instead.
    if (single) fragments.append(this.#buildOlfactiveBox(this.#groups[0].fragrance));

    fragments.append(this.#buildToolbar());

    let total = 0;
    for (const group of this.#groups) {
      fragments.append(this.#buildGroup(group, single));
      total += group.offers.length;
    }

    const explorer = this.#buildNoteExplorer();
    if (explorer) fragments.append(explorer);

    this.resultsBody.replaceChildren(fragments);
    // On a fallback the heading must not name the scent on screen — it was not what was searched
    // for, and titling the panel with it would contradict the notice directly above.
    this.#setHeading(single && !this.#fallback ? this.#groups[0].fragrance : null);
    this.#announce(
      this.#fallback ? this.#t('similar_title', { query: this.#fallback.query }) : this.#t('results_count', { count: total })
    );
  }

  /**
   * The "these are not what you searched for" banner.
   *
   * @returns {HTMLElement|null} null on an ordinary search
   */
  #buildFallbackNotice() {
    if (!this.#fallback) return null;

    const wrap = el('div', 'pb-comparator__notice');
    wrap.setAttribute('role', 'status');
    wrap.append(el('p', 'pb-comparator__notice-title', this.#t('similar_title', { query: this.#fallback.query })));
    wrap.append(
      el(
        'p',
        'pb-comparator__notice-body',
        this.#t(this.#fallback.reason === 'no_offers' ? 'similar_body_notes' : 'similar_body_popular')
      )
    );

    return wrap;
  }

  /**
   * Notes from whatever is on screen, as chips that re-search on that note.
   *
   * Sits at the foot of the results because it is a next step, not a filter — the customer has
   * found what they searched for, and this offers a sideways move into the same olfactive
   * territory. Notes are deduplicated case-insensitively and capped, since a full pyramid across
   * several groups would run to dozens.
   *
   * @returns {HTMLElement|null} null when nothing on screen carries notes
   */
  #buildNoteExplorer() {
    /** @type {Map<string, string>} */
    const notes = new Map();

    for (const group of this.#groups) {
      for (const note of this.#notesFor(group.fragrance)) {
        const key = normalise(note);
        if (key && !notes.has(key)) notes.set(key, note);
      }
      if (notes.size >= MAX_NOTE_CHIPS) break;
    }

    if (notes.size < 2) return null;

    const section = el('section', 'pb-comparator__notes-explorer');
    section.append(el('p', 'pb-comparator__notes-explorer-title', this.#t('explore_by_note')));

    const list = el('div', 'pb-comparator__note-chips');
    for (const label of [...notes.values()].slice(0, MAX_NOTE_CHIPS)) {
      const chip = el('button', 'pb-comparator__note-chip', label);
      chip.type = 'button';
      chip.dataset.pbNote = label;
      list.append(chip);
    }

    section.append(list);
    return section;
  }

  /**
   * One original, rendered as a reference header, with our interpretations of it beneath.
   *
   * @param {Group} group
   * @param {boolean} single - true when this is the only group on screen
   * @returns {HTMLElement}
   */
  #buildGroup(group, single) {
    const { fragrance } = group;
    const section = el('section', 'pb-comparator__group');

    section.append(this.#buildOriginalHeader(fragrance, single));

    const offers = group.offers.slice();
    offers.sort((a, b) => (this.#sort === 'priciest' ? b.price - a.price : a.price - b.price));

    const grid = el('div', `pb-comparator__grid pb-comparator__grid--${this.#layout}`);
    for (const offer of offers) grid.append(this.#buildOfferCard(fragrance, offer));
    section.append(grid);

    return section;
  }

  /**
   * @param {Fragrance|null} fragrance
   */
  #setHeading(fragrance) {
    const genderPill = this.querySelector('[data-pb-gender]');
    if (!genderPill) return;

    const gender = (fragrance?.gender || '').toLowerCase();
    const label = gender ? this.#t(`gender_${gender}`) : '';

    genderPill.textContent = label;
    genderPill.hidden = !label || label === `gender_${gender}`;
  }

  /**
   * Flattened notes, top through base, sourced from the product's note-group entries.
   *
   * @param {Fragrance} fragrance
   * @returns {string[]}
   */
  #notesFor(fragrance) {
    const own = [
      ...(fragrance.notes?.top || []),
      ...(fragrance.notes?.heart || []),
      ...(fragrance.notes?.base || []),
    ].filter(Boolean);

    return own;
  }

  #buildOlfactiveBox(fragrance) {
    const box = el('div', 'pb-comparator__olfactive');
    const notes = this.#notesFor(fragrance);

    box.append(el('p', 'pb-comparator__olfactive-title', this.#t('notes_intro')));

    if (notes.length) {
      box.append(el('p', 'pb-comparator__olfactive-notes', notes.join(' · ')));
    } else if (fragrance.family) {
      box.append(el('p', 'pb-comparator__olfactive-notes', fragrance.family));
    }

    box.append(el('p', 'pb-comparator__olfactive-footnote', this.#t('notes_recommend')));
    return box;
  }

  #buildToolbar() {
    const toolbar = el('div', 'pb-comparator__toolbar');

    const sortGroup = el('div', 'pb-comparator__sort');
    sortGroup.setAttribute('role', 'radiogroup');
    sortGroup.setAttribute('aria-label', this.#t('sort_label'));
    sortGroup.append(el('span', 'pb-comparator__sort-label', this.#t('sort_label')));

    for (const value of ['cheapest', 'priciest']) {
      const button = el('button', 'pb-comparator__pill', this.#t(value));
      button.type = 'button';
      button.dataset.pbSort = value;
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', String(this.#sort === value));
      if (this.#sort === value) button.classList.add('is-active');
      sortGroup.append(button);
    }

    const layoutGroup = el('div', 'pb-comparator__layout-toggle');
    for (const value of ['list', 'grid']) {
      const button = el('button', 'pb-comparator__icon-button');
      button.type = 'button';
      button.dataset.pbLayout = value;
      button.setAttribute('aria-label', this.#t(`layout_${value}`));
      button.setAttribute('aria-pressed', String(this.#layout === value));
      if (this.#layout === value) button.classList.add('is-active');
      button.append(el('span', `pb-comparator__icon pb-comparator__icon--${value}`));
      layoutGroup.append(button);
    }

    toolbar.append(sortGroup, layoutGroup);
    return toolbar;
  }

  /**
   * The original, as a reference header above our interpretations of it.
   *
   * It is deliberately not a card and carries no button or link: it is the yardstick the prices
   * below are measured against, not something the customer can buy from us.
   *
   * @param {Fragrance} fragrance
   * @param {boolean} single - notes are shown in the olfactive box instead when true
   * @returns {HTMLElement}
   */
  #buildOriginalHeader(fragrance, single) {
    const header = el('header', 'pb-comparator__original');

    const identity = el('div', 'pb-comparator__original-identity');

    const imageUrl = safeImageUrl(fragrance.image);
    if (imageUrl) {
      const img = el('img', 'pb-comparator__original-thumb');
      img.src = imageUrl;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 44;
      img.height = 44;
      identity.append(img);
    }

    const naming = el('div', 'pb-comparator__original-naming');
    naming.append(el('span', 'pb-comparator__original-eyebrow', this.#t('the_original')));

    // Brand and scent split onto separate lines. Run together they read as one long string;
    // apart, the house carries the recognition the customer actually searched for.
    if (fragrance.brand) {
      naming.append(el('span', 'pb-comparator__original-brand', fragrance.brand));
    }
    naming.append(el('h3', 'pb-comparator__original-title', fragrance.name));
    identity.append(naming);
    header.append(identity);

    const facts = [];
    if (fragrance.rrp > 0) facts.push(this.#money(fragrance.rrp));
    if (fragrance.rrp_size > 0) facts.push(`${fragrance.rrp_size} ml`);
    if (fragrance.rrp > 0 && fragrance.rrp_size > 0) {
      facts.push(this.#t('per_ml', { amount: this.#money(fragrance.rrp / fragrance.rrp_size) }));
    }
    if (facts.length) header.append(el('p', 'pb-comparator__original-price', facts.join(' · ')));

    // On a multi-result view each group carries its own one-line notes summary, since the shared
    // olfactive box at the top would otherwise have to describe several fragrances at once.
    if (!single) {
      const notes = this.#notesFor(fragrance);
      const summary = notes.length ? notes.slice(0, 5).join(' · ') : fragrance.family;
      if (summary) header.append(el('p', 'pb-comparator__original-notes', summary));
    }

    if (fragrance.rrp_note) {
      header.append(el('p', 'pb-comparator__original-note', fragrance.rrp_note));
    }

    return header;
  }

  #buildOfferCard(fragrance, offer) {
    const card = el('article', 'pb-comparator__card pb-comparator__card--own');

    // The reference puts the retailer at the head of every card. Ours are all from one shop, so
    // the useful equivalent is the house the scent interprets.
    if (fragrance.brand) {
      card.append(el('p', 'pb-comparator__card-brand', fragrance.brand));
    }

    const media = el('div', 'pb-comparator__media');
    const imageUrl = safeImageUrl(offer.image);
    if (imageUrl) {
      const img = el('img', 'pb-comparator__image');
      img.src = imageUrl;
      img.alt = offer.title;
      img.loading = 'lazy';
      img.width = 200;
      img.height = 200;
      media.append(img);
    } else {
      media.append(el('span', 'pb-comparator__image-placeholder'));
    }
    card.append(media);

    // Every card in a group interprets the same original, so repeating "… Inspired By Sauvage"
    // on each is noise. The product code is what distinguishes CAR129 from BEY129, so that
    // leads, with the full name kept on the link and as a tooltip.
    const heading = el('h3', 'pb-comparator__card-title', offer.code || offer.title);
    heading.title = offer.title;
    card.append(heading);

    card.append(el('p', 'pb-comparator__price', this.#money(offer.price)));

    const size = offer.size_ml > 0 ? offer.size_ml : this.#defaultProductSize;
    const meta = [];
    if (offer.variant_title) meta.push(offer.variant_title);
    else if (size > 0) meta.push(`${size} ml`);
    if (size > 0) meta.push(this.#t('per_ml', { amount: this.#money(offer.price / size) }));
    if (meta.length) card.append(el('p', 'pb-comparator__card-meta', meta.join(' · ')));

    const savings = this.#savings(fragrance, offer);
    if (savings) {
      card.append(
        el(
          'span',
          'pb-comparator__badge pb-comparator__badge--save',
          this.#t('save_amount', { amount: this.#money(savings.saving), percent: savings.percent })
        )
      );
    }

    const button = el('button', 'pb-comparator__cta');
    button.type = 'button';
    if (offer.available) {
      button.dataset.pbAdd = String(offer.variant_id);
      button.dataset.pbProductId = String(offer.product_id);
      button.textContent = this.#t('add_to_cart');
    } else {
      button.disabled = true;
      button.textContent = this.#t('sold_out');
    }
    card.append(button);

    const link = el('a', 'pb-comparator__link', this.#t('view_product'));
    link.href = offer.url;
    link.dataset.pbProductLink = '';
    // The visible text is the same on every card, so give assistive tech the product it opens.
    link.setAttribute('aria-label', `${this.#t('view_product')} — ${offer.title}`);
    card.append(link);

    return card;
  }

  /**
   * We recognised the fragrance but carry no interpretation of it. Distinct from "no results",
   * which means we did not recognise the search at all.
   */
  #renderNoMatch() {
    if (!this.resultsBody) return;

    const wrap = el('div', 'pb-comparator__state');
    wrap.append(el('p', 'pb-comparator__state-title', this.#t('no_match_title')));
    wrap.append(el('p', 'pb-comparator__state-body', this.#t('no_match_body')));

    this.resultsBody.replaceChildren(wrap);
    this.#setHeading(null);
    this.#showResultsScreen();
    this.#announce(this.#t('no_match_title'));
  }

  #renderNoResults(query) {
    if (!this.resultsBody) return;

    const wrap = el('div', 'pb-comparator__state');
    wrap.append(el('p', 'pb-comparator__state-title', this.#t('no_results_title')));
    wrap.append(el('p', 'pb-comparator__state-body', this.#t('no_results_body', { query })));

    this.resultsBody.replaceChildren(wrap);
    this.#setHeading(null);
    this.#announce(this.#t('no_results_title'));
  }

  #renderError() {
    if (!this.resultsBody) return;

    const wrap = el('div', 'pb-comparator__state');
    wrap.append(el('p', 'pb-comparator__state-title', this.#t('error_title')));
    wrap.append(el('p', 'pb-comparator__state-body', this.#t('error_body')));

    const retry = el('button', 'pb-comparator__cta', this.#t('try_again'));
    retry.type = 'button';
    retry.addEventListener('click', () => {
      this.#loadPromise = null;
      this.#showSearchScreen();
      this.#warm();
    });
    wrap.append(retry);

    this.resultsBody.replaceChildren(wrap);
    this.#showResultsScreen();
  }

  /**
   * @param {HTMLButtonElement} button
   */
  async #addToCart(button) {
    const variantId = Number(button.dataset.pbAdd);

    // Never trust the DOM alone — the id must correspond to an offer we actually loaded.
    const offer = this.#offers.find((candidate) => candidate.variant_id === variantId);
    if (!offer || !offer.available || button.disabled) return;

    button.disabled = true;
    button.classList.add('is-loading');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((node) => node.dataset.sectionId)
      .filter(Boolean);

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              // Underscore-prefixed: hidden from the customer, visible in order exports.
              properties: { _comparator_source: 'true' },
            },
          ],
          sections: sectionIds.join(','),
        }),
      });

      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Cart error');

      document.body.dispatchEvent(
        new CartAddEvent(undefined, 'pb-comparator', {
          source: 'pb-comparator',
          itemCount: 1,
          productId: button.dataset.pbProductId,
          variantId: String(variantId),
          sections: result.sections,
        })
      );

      button.classList.remove('is-loading');
      button.classList.add('is-added');
      button.textContent = this.#t('added');
      this.#announce(this.#t('added'));
      this.#track('own_product_added_to_cart', {
        variant_id: variantId,
        product_id: offer.product_id,
        fragrance: offer.fragrance,
        value: offer.price / 100,
      });

      window.setTimeout(() => {
        button.classList.remove('is-added');
        button.textContent = this.#t('add_to_cart');
        button.disabled = false;
      }, ADDED_FEEDBACK_MS);
    } catch {
      button.classList.remove('is-loading');
      button.textContent = this.#t('cart_error');
      this.#announce(this.#t('cart_error'));

      window.setTimeout(() => {
        button.textContent = this.#t('add_to_cart');
        button.disabled = false;
      }, ADDED_FEEDBACK_MS);
    }
  }

  /**
   * @param {string} message
   */
  #announce(message) {
    if (this.statusRegion) this.statusRegion.textContent = message;
  }

  /**
   * @param {string} name
   * @param {Record<string, unknown>} [payload]
   */
  #track(name, payload = {}) {
    this.dispatchEvent(new CustomEvent(`pb-comparator:${name}`, { bubbles: true, detail: payload }));

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: name, ...payload });
    }
  }
}

if (!customElements.get('pb-comparator')) {
  customElements.define('pb-comparator', PbComparator);
}
